import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Globe, Save, Upload, Plus, Trash2, Image as ImageIcon } from 'lucide-react';

export default function WebsiteCMS() {
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('landing');
  const [content, setContent] = useState({
    landing: {
      hero_title: '',
      hero_subtitle: '',
      hero_cta: '',
      hero_image: null,
      announcement_bar: {
        enabled: false,
        text: '',
        background_color: '#10b981',
        text_color: '#ffffff',
        timeout: 0
      },
      ad_bars: []
    },
    about: { title: '', description: '' },
    contact: { email: '', phone: '', address: '' },
    footer: { 
      copyright_text: '© 2024 ASSETCO. All rights reserved.',
      background_color: '#0f172a',
      text_color: '#ffffff',
      social_links: {
        facebook: '',
        instagram: '',
        twitter: '',
        tiktok: '',
        linkedin: ''
      },
      partner_logos: []
    },
    settings: {
      page_title: 'ASSETCO - Your Business Partner',
      favicon: null,
      app_icon: null
    }
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const pages = ['landing', 'about', 'contact', 'footer', 'settings'];
      const promises = pages.map(page =>
        axios.get(`${API}/website/content/${page}`)
      );
      const responses = await Promise.all(promises);
      
      const newContent = {};
      responses.forEach((response, index) => {
        const pageContent = response.data.content || {};
        if (pages[index] === 'landing') {
          newContent.landing = {
            hero_title: pageContent.hero_title || '',
            hero_subtitle: pageContent.hero_subtitle || '',
            hero_cta: pageContent.hero_cta || '',
            hero_image: pageContent.hero_image || null,
            announcement_bar: pageContent.announcement_bar || {
              enabled: false,
              text: '',
              background_color: '#10b981',
              text_color: '#ffffff',
              timeout: 0
            },
            ad_bars: pageContent.ad_bars || []
          };
        } else if (pages[index] === 'settings') {
          newContent.settings = {
            page_title: pageContent.page_title || 'ASSETCO - Your Business Partner',
            favicon: pageContent.favicon || null,
            app_icon: pageContent.app_icon || null
          };
        } else {
          newContent[pages[index]] = pageContent;
        }
      });
      setContent(newContent);
    } catch (error) {
      toast.error('Failed to load content');
    }
  };

  const handleImageUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !(/\.(jpe?g|png|gif|webp|svg|bmp|ico|tiff?|avif|heic|heif)$/i.test(file.name))) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Handle nested field paths like 'settings.favicon'
      if (field.includes('.')) {
        const [page, fieldName] = field.split('.');
        updateContent(page, fieldName, response.data.image_url);
      } else {
        updateContent('landing', field, response.data.image_url);
      }
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const addAdBar = () => {
    const newAdBar = {
      id: Date.now().toString(),
      type: 'image',
      content: '',
      image_url: null,
      video_url: null,
      link: '',
      position: 'top',
      enabled: true,
      countdown_enabled: false,
      countdown_end_date: null,
      carousel_enabled: false,
      carousel_images: []
    };
    
    setContent({
      ...content,
      landing: {
        ...content.landing,
        ad_bars: [...content.landing.ad_bars, newAdBar]
      }
    });
  };

  const removeAdBar = (id) => {
    setContent({
      ...content,
      landing: {
        ...content.landing,
        ad_bars: content.landing.ad_bars.filter(bar => bar.id !== id)
      }
    });
  };

  const updateAdBar = (id, field, value) => {
    setContent({
      ...content,
      landing: {
        ...content.landing,
        ad_bars: content.landing.ad_bars.map(bar =>
          bar.id === id ? { ...bar, [field]: value } : bar
        )
      }
    });
  };

  const uploadCarouselImages = async (id, files) => {
    if (!files || files.length === 0) return;

    const uploadPromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });

    const imageUrls = await Promise.all(uploadPromises);
    const currentImages = content.landing.ad_bars.find(bar => bar.id === id)?.carousel_images || [];
    updateAdBar(id, 'carousel_images', [...currentImages, ...imageUrls]);
  };

  const uploadAdBarImage = async (id, file) => {
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      updateAdBar(id, 'image_url', response.data.image_url);
      toast.success('Ad bar image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveContent = async (pageName) => {
    setLoading(true);
    try {
      await axios.put(`${API}/website/content`, {
        page_name: pageName,
        content: content[pageName]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${pageName.charAt(0).toUpperCase() + pageName.slice(1)} page updated successfully`);
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setLoading(false);
    }
  };

  const updateContent = (page, field, value) => {
    setContent({
      ...content,
      [page]: {
        ...content[page],
        [field]: value
      }
    });
  };

  const updateAnnouncementBar = (field, value) => {
    setContent({
      ...content,
      landing: {
        ...content.landing,
        announcement_bar: {
          ...content.landing.announcement_bar,
          [field]: value
        }
      }
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="website-cms-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Website CMS</h1>
            <p className="text-slate-600 mt-1">Manage your website content, ads, and announcements</p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Globe className="w-5 h-5" />
            View Website
          </a>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="landing">Landing Page</TabsTrigger>
            <TabsTrigger value="about">About Page</TabsTrigger>
            <TabsTrigger value="contact">Contact Page</TabsTrigger>
            <TabsTrigger value="footer">Footer</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="landing" className="space-y-6">
            {/* Hero Section */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Hero Section</h2>
              <div className="space-y-4">
                <div>
                  <Label>Hero Image</Label>
                  {content.landing.hero_image && (
                    <div className="mt-2 mb-4">
                      <img
                        src={content.landing.hero_image}
                        alt="Hero"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*,.webp,.svg,.avif,.heic,.heif"
                    onChange={(e) => handleImageUpload(e, 'hero_image')}
                    className="hidden"
                    id="hero-image-upload"
                  />
                  <label htmlFor="hero-image-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('hero-image-upload').click()}
                      disabled={uploadingImage}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingImage ? 'Uploading...' : 'Upload Hero Image'}
                    </Button>
                  </label>
                </div>
                <div>
                  <Label>Hero Title</Label>
                  <Input
                    value={content.landing.hero_title}
                    onChange={(e) => updateContent('landing', 'hero_title', e.target.value)}
                    placeholder="Welcome to AN-NISA Nexus"
                  />
                </div>
                <div>
                  <Label>Hero Subtitle</Label>
                  <Textarea
                    value={content.landing.hero_subtitle}
                    onChange={(e) => updateContent('landing', 'hero_subtitle', e.target.value)}
                    placeholder="Your all-in-one business management solution"
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Call to Action Text</Label>
                  <Input
                    value={content.landing.hero_cta}
                    onChange={(e) => updateContent('landing', 'hero_cta', e.target.value)}
                    placeholder="Get Started"
                  />
                </div>
              </div>
            </Card>

            {/* Announcement Bar */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Announcement Bar</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Enable Announcement Bar</p>
                    <p className="text-sm text-slate-600">Show a top banner with important announcements</p>
                  </div>
                  <Switch
                    checked={content.landing.announcement_bar.enabled}
                    onCheckedChange={(checked) => updateAnnouncementBar('enabled', checked)}
                  />
                </div>
                {content.landing.announcement_bar.enabled && (
                  <>
                    <div>
                      <Label>Announcement Text</Label>
                      <Input
                        value={content.landing.announcement_bar.text}
                        onChange={(e) => updateAnnouncementBar('text', e.target.value)}
                        placeholder="Special offer: 20% off all products!"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Background Color</Label>
                        <Input
                          type="color"
                          value={content.landing.announcement_bar.background_color}
                          onChange={(e) => updateAnnouncementBar('background_color', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Text Color</Label>
                        <Input
                          type="color"
                          value={content.landing.announcement_bar.text_color}
                          onChange={(e) => updateAnnouncementBar('text_color', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Timeout (seconds, 0=permanent)</Label>
                        <Input
                          type="number"
                          value={content.landing.announcement_bar.timeout}
                          onChange={(e) => updateAnnouncementBar('timeout', parseInt(e.target.value))}
                          min="0"
                        />
                      </div>
                    </div>
                    {/* Preview */}
                    <div className="mt-4">
                      <Label>Preview:</Label>
                      <div
                        className="mt-2 p-4 text-center font-medium rounded-lg"
                        style={{
                          backgroundColor: content.landing.announcement_bar.background_color,
                          color: content.landing.announcement_bar.text_color
                        }}
                      >
                        {content.landing.announcement_bar.text || 'Your announcement will appear here'}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* Ad Bars */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-900">Ad Bars</h2>
                <Button onClick={addAdBar} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Ad Bar
                </Button>
              </div>
              {content.landing.ad_bars.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No ad bars yet. Click "Add Ad Bar" to create one.</p>
              ) : (
                <div className="space-y-4">
                  {content.landing.ad_bars.map((adBar) => (
                    <div key={adBar.id} className="p-4 border-2 border-slate-200 rounded-lg space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <ImageIcon className="w-5 h-5 text-emerald-600" />
                          <span className="font-medium text-slate-900">Ad Bar</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={adBar.enabled}
                            onCheckedChange={(checked) => updateAdBar(adBar.id, 'enabled', checked)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAdBar(adBar.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Ad Type</Label>
                          <Select
                            value={adBar.type}
                            onValueChange={(value) => updateAdBar(adBar.id, 'type', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="image">Image</SelectItem>
                              <SelectItem value="video">Video</SelectItem>
                              <SelectItem value="text">Text Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Position</Label>
                          <Select
                            value={adBar.position}
                            onValueChange={(value) => updateAdBar(adBar.id, 'position', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="top">Top of Page</SelectItem>
                              <SelectItem value="middle">Middle</SelectItem>
                              <SelectItem value="bottom">Bottom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {adBar.type === 'image' && (
                        <div>
                          <Label>Image Upload</Label>
                          {adBar.image_url && (
                            <img
                              src={adBar.image_url}
                              alt="Ad"
                              className="w-full h-32 object-cover rounded-lg mt-2 mb-2"
                            />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => uploadAdBarImage(adBar.id, e.target.files[0])}
                            className="hidden"
                            id={`ad-image-${adBar.id}`}
                          />
                          <label htmlFor={`ad-image-${adBar.id}`}>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              onClick={() => document.getElementById(`ad-image-${adBar.id}`).click()}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Image
                            </Button>
                          </label>
                        </div>
                      )}

                      {adBar.type === 'video' && (
                        <div>
                          <Label>Video URL (YouTube, Vimeo, etc.)</Label>
                          <Input
                            value={adBar.video_url || ''}
                            onChange={(e) => updateAdBar(adBar.id, 'video_url', e.target.value)}
                            placeholder="https://www.youtube.com/embed/..."
                          />
                        </div>
                      )}

                      <div>
                        <Label>Text/Wording</Label>
                        <Input
                          value={adBar.content}
                          onChange={(e) => updateAdBar(adBar.id, 'content', e.target.value)}
                          placeholder="Ad text or caption"
                        />
                      </div>

                      <div>
                        <Label>Link URL (optional)</Label>
                        <Input
                          value={adBar.link}
                          onChange={(e) => updateAdBar(adBar.id, 'link', e.target.value)}
                          placeholder="https://example.com"
                        />
                      </div>

                      {/* Countdown Timer */}
                      <div className="border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label>Countdown Timer</Label>
                          <Switch
                            checked={adBar.countdown_enabled || false}
                            onCheckedChange={(checked) => updateAdBar(adBar.id, 'countdown_enabled', checked)}
                          />
                        </div>
                        {adBar.countdown_enabled && (
                          <div>
                            <Label>End Date & Time</Label>
                            <Input
                              type="datetime-local"
                              value={adBar.countdown_end_date || ''}
                              onChange={(e) => updateAdBar(adBar.id, 'countdown_end_date', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        )}
                      </div>

                      {/* Carousel */}
                      {adBar.type === 'image' && (
                        <div className="border-t pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <Label>Image Carousel (Multiple Images)</Label>
                            <Switch
                              checked={adBar.carousel_enabled || false}
                              onCheckedChange={(checked) => updateAdBar(adBar.id, 'carousel_enabled', checked)}
                            />
                          </div>
                          {adBar.carousel_enabled && (
                            <div className="space-y-2">
                              <p className="text-xs text-slate-600">Upload multiple images for carousel</p>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => uploadCarouselImages(adBar.id, Array.from(e.target.files))}
                                className="hidden"
                                id={`carousel-${adBar.id}`}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => document.getElementById(`carousel-${adBar.id}`).click()}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Add Carousel Images
                              </Button>
                              {adBar.carousel_images && adBar.carousel_images.length > 0 && (
                                <div className="grid grid-cols-4 gap-2 mt-2">
                                  {adBar.carousel_images.map((img, idx) => (
                                    <div key={idx} className="relative">
                                      <img src={img} alt={`Slide ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                                      <button
                                        onClick={() => {
                                          const newImages = adBar.carousel_images.filter((_, i) => i !== idx);
                                          updateAdBar(adBar.id, 'carousel_images', newImages);
                                        }}
                                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Button
              onClick={() => saveContent('landing')}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
              data-testid="save-landing-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Landing Page'}
            </Button>
          </TabsContent>

          <TabsContent value="about" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">About Page Content</h2>
              <div className="space-y-4">
                <div>
                  <Label>Page Title</Label>
                  <Input
                    value={content.about.title}
                    onChange={(e) => updateContent('about', 'title', e.target.value)}
                    placeholder="About Us"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={content.about.description}
                    onChange={(e) => updateContent('about', 'description', e.target.value)}
                    placeholder="Tell your story..."
                    rows={6}
                  />
                </div>
                <Button
                  onClick={() => saveContent('about')}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-about-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save About Page'}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="contact" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Contact Page Content</h2>
              <div className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={content.contact.email}
                    onChange={(e) => updateContent('contact', 'email', e.target.value)}
                    placeholder="contact@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={content.contact.phone}
                    onChange={(e) => updateContent('contact', 'phone', e.target.value)}
                    placeholder="+1 234 567 890"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={content.contact.address}
                    onChange={(e) => updateContent('contact', 'address', e.target.value)}
                    placeholder="123 Business St, City, Country"
                    rows={3}
                  />
                </div>
                <Button
                  onClick={() => saveContent('contact')}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-contact-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Contact Page'}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="footer" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Website Footer</h2>
              <div className="space-y-4">
                <div>
                  <Label>Copyright Text</Label>
                  <Input
                    value={content.footer?.copyright_text || ''}
                    onChange={(e) => updateContent('footer', 'copyright_text', e.target.value)}
                    placeholder="© 2024 ASSETCO. All rights reserved."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    This text appears at the bottom of your website
                  </p>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Footer Colors</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Background Color</Label>
                      <div className="flex gap-2 items-center mt-1">
                        <Input
                          type="color"
                          value={content.footer?.background_color || '#0f172a'}
                          onChange={(e) => updateContent('footer', 'background_color', e.target.value)}
                          className="w-20 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          type="text"
                          value={content.footer?.background_color || '#0f172a'}
                          onChange={(e) => updateContent('footer', 'background_color', e.target.value)}
                          placeholder="#0f172a"
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Footer background color
                      </p>
                    </div>
                    <div>
                      <Label>Text Color</Label>
                      <div className="flex gap-2 items-center mt-1">
                        <Input
                          type="color"
                          value={content.footer?.text_color || '#ffffff'}
                          onChange={(e) => updateContent('footer', 'text_color', e.target.value)}
                          className="w-20 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          type="text"
                          value={content.footer?.text_color || '#ffffff'}
                          onChange={(e) => updateContent('footer', 'text_color', e.target.value)}
                          placeholder="#ffffff"
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Footer text color
                      </p>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="mt-4 p-4 rounded-lg border" style={{ 
                    backgroundColor: content.footer?.background_color || '#0f172a',
                    color: content.footer?.text_color || '#ffffff'
                  }}>
                    <p className="text-sm text-center">Preview: {content.footer?.copyright_text || '© 2024 ASSETCO. All rights reserved.'}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Social Media Links (Optional)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Facebook URL</Label>
                      <Input
                        value={content.footer?.social_links?.facebook || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.footer.social_links) newContent.footer.social_links = {};
                          newContent.footer.social_links.facebook = e.target.value;
                          setContent(newContent);
                        }}
                        placeholder="https://facebook.com/yourpage"
                      />
                    </div>
                    <div>
                      <Label>Instagram URL</Label>
                      <Input
                        value={content.footer?.social_links?.instagram || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.footer.social_links) newContent.footer.social_links = {};
                          newContent.footer.social_links.instagram = e.target.value;
                          setContent(newContent);
                        }}
                        placeholder="https://instagram.com/yourprofile"
                      />
                    </div>
                    <div>
                      <Label>Twitter URL</Label>
                      <Input
                        value={content.footer?.social_links?.twitter || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.footer.social_links) newContent.footer.social_links = {};
                          newContent.footer.social_links.twitter = e.target.value;
                          setContent(newContent);
                        }}
                        placeholder="https://twitter.com/yourhandle"
                      />
                    </div>
                    <div>
                      <Label>TikTok URL</Label>
                      <Input
                        value={content.footer?.social_links?.tiktok || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.footer.social_links) newContent.footer.social_links = {};
                          newContent.footer.social_links.tiktok = e.target.value;
                          setContent(newContent);
                        }}
                        placeholder="https://tiktok.com/@yourprofile"
                      />
                    </div>
                    <div>
                      <Label>LinkedIn URL</Label>
                      <Input
                        value={content.footer?.social_links?.linkedin || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.footer.social_links) newContent.footer.social_links = {};
                          newContent.footer.social_links.linkedin = e.target.value;
                          setContent(newContent);
                        }}
                        placeholder="https://linkedin.com/company/yourcompany"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Partner Logos</h3>
                  <p className="text-sm text-slate-500 mb-3">
                    Upload logos of partner companies to display in the footer
                  </p>
                  
                  <div className="space-y-3">
                    {content.footer?.partner_logos?.map((logo, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                        <img src={logo} alt={`Partner ${index + 1}`} className="h-12 w-auto object-contain" />
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            const newContent = { ...content };
                            newContent.footer.partner_logos = newContent.footer.partner_logos.filter((_, i) => i !== index);
                            setContent(newContent);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error('Image size should be less than 2MB');
                            return;
                          }
                          
                          try {
                            setUploadingImage(true);
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await axios.post(`${API}/upload/image`, formData, {
                              headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                              }
                            });
                            
                            const newContent = { ...content };
                            if (!newContent.footer.partner_logos) newContent.footer.partner_logos = [];
                            newContent.footer.partner_logos.push(response.data.image_url);
                            setContent(newContent);
                            
                            toast.success('Logo uploaded successfully');
                            e.target.value = '';
                          } catch (error) {
                            toast.error('Failed to upload logo');
                          } finally {
                            setUploadingImage(false);
                          }
                        }}
                        className="hidden"
                        id="partner-logo-upload"
                      />
                      <label htmlFor="partner-logo-upload">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={uploadingImage}
                          onClick={() => document.getElementById('partner-logo-upload').click()}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingImage ? 'Uploading...' : 'Add Partner Logo'}
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => saveContent('footer')}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Footer'}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Website Settings</h2>
              <div className="space-y-6">
                {/* Page Title */}
                <div>
                  <Label>Page Title *</Label>
                  <Input
                    value={content.settings.page_title}
                    onChange={(e) => setContent({
                      ...content,
                      settings: { ...content.settings, page_title: e.target.value }
                    })}
                    placeholder="Enter website page title"
                    className="mt-2"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    This will appear in the browser tab and search results
                  </p>
                </div>

                {/* Favicon Upload */}
                <div>
                  <Label>Favicon</Label>
                  <div className="mt-2 space-y-3">
                    {content.settings.favicon && (
                      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border">
                        <img
                          src={content.settings.favicon}
                          alt="Favicon preview"
                          className="w-16 h-16 object-contain border rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">Current Favicon</p>
                          <p className="text-xs text-slate-500">32x32 or 64x64 pixels recommended</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setContent({
                            ...content,
                            settings: { ...content.settings, favicon: null }
                          })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('favicon-upload').click()}
                        disabled={uploadingImage}
                        className="flex-1"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingImage ? 'Uploading...' : 'Upload Favicon'}
                      </Button>
                      <input
                        id="favicon-upload"
                        type="file"
                        accept="image/png,image/x-icon,image/svg+xml,image/jpeg"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, 'settings.favicon')}
                      />
                    </div>
                    <p className="text-sm text-slate-500">
                      Upload a square image (PNG, ICO, or SVG). Recommended size: 32x32 or 64x64 pixels
                    </p>
                  </div>
                </div>

                {/* App Icon Upload (PWA) */}
                <div>
                  <Label>App Icon (Mobile)</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-2">
                    This icon appears on the home screen when users install the app on their phone
                  </p>
                  <div className="mt-2 space-y-3">
                    {content.settings.app_icon && (
                      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border">
                        <img
                          src={content.settings.app_icon}
                          alt="App Icon preview"
                          className="w-20 h-20 object-contain border rounded-2xl"
                          data-testid="app-icon-preview"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">Current App Icon</p>
                          <p className="text-xs text-slate-500">512x512 pixels recommended</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setContent({
                            ...content,
                            settings: { ...content.settings, app_icon: null }
                          })}
                          data-testid="remove-app-icon-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('app-icon-upload').click()}
                        disabled={uploadingImage}
                        className="flex-1"
                        data-testid="upload-app-icon-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingImage ? 'Uploading...' : 'Upload App Icon'}
                      </Button>
                      <input
                        id="app-icon-upload"
                        type="file"
                        accept="image/*,.webp,.svg,.avif"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, 'settings.app_icon')}
                      />
                    </div>
                    <p className="text-sm text-slate-500">
                      Upload a square image (PNG recommended). Size: 512x512 pixels for best quality
                    </p>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  onClick={() => saveContent('settings')}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}